import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../LoadingSpinner';
import MirrorNotice from './MirrorNotice';
import './Operations.css';
import './LowStockDashboard.css';

const BRANDS = ['Rewined', 'Candlefish', 'Other'];

// A per-location cell is null (not 0) whenever the server could not answer the
// split. During a NetSuite outage no offline source knows it, and rendering 0
// there would read as "none at that location".
const qty = (v) => (v === null || v === undefined ? '—' : Math.round(v));

function StockByLocationDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('sku');
  const [sortDir, setSortDir] = useState('asc');
  const [collapsed, setCollapsed] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get('/api/inventory/stock-by-location');
      setData(res.data);
      setSelected(new Set());
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load stock-by-location data');
    } finally {
      setLoading(false);
    }
  };

  const toggleCollapse = (brand) =>
    setCollapsed((prev) => ({ ...prev, [brand]: !prev[brand] }));

  const toggleItem = (sku, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleInactivate = async () => {
    if (!window.confirm(`Mark ${selected.size} item${selected.size === 1 ? '' : 's'} as inactive? This updates the local database.`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post('/api/inventory/bulk-inactivate', { skus: [...selected] });
      if (res.data.failed?.length) {
        alert(`${res.data.updated} inactivated. ${res.data.failed.length} failed in NetSuite:\n${res.data.failed.join('\n')}`);
      }
      await fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to inactivate items');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filteredBrands = useMemo(() => {
    if (!data) return {};
    const q = search.toLowerCase();
    const result = {};
    for (const brand of BRANDS) {
      const items = (data.brands[brand] || []).filter(
        (item) => !q || item.sku.toLowerCase().includes(q) || item.name.toLowerCase().includes(q)
      );
      const sorted = [...items].sort((a, b) => {
        const av = a[sortKey] ?? 0;
        const bv = b[sortKey] ?? 0;
        if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        return sortDir === 'asc' ? av - bv : bv - av;
      });
      result[brand] = sorted;
    }
    return result;
  }, [data, search, sortKey, sortDir]);

  const totalVisible = useMemo(
    () => BRANDS.reduce((sum, b) => sum + (filteredBrands[b]?.length || 0), 0),
    [filteredBrands]
  );

  if (loading) return <div className="ops-page"><LoadingSpinner /></div>;

  if (error) return (
    <div className="ops-page">
      <div className="ops-page-header">
        <button className="back-button" onClick={() => navigate('/ops')}>← Back</button>
        <h1>Stock by Location</h1>
      </div>
      <div className="message-box error">{error}</div>
    </div>
  );

  const asOf = data?.as_of ? new Date(data.as_of).toLocaleString() : '';

  return (
    <div className="ops-page low-stock-page">
      <div className="ops-page-header">
        <button className="back-button" onClick={() => navigate('/ops')}>← Back</button>
        <div className="low-stock-title-block">
          <h1>Stock by Location</h1>
          {asOf && <span className="low-stock-as-of">as of {asOf}</span>}
        </div>
        <button className="low-stock-refresh" onClick={fetchData}>Refresh</button>
      </div>

      <MirrorNotice data={data} />

      <div className="low-stock-summary-row">
        {BRANDS.map((brand) => {
          const count = filteredBrands[brand]?.length || 0;
          return (
            <div key={brand} className="low-stock-summary-chip">
              <span className="low-stock-summary-brand">{brand}</span>
              <span className="low-stock-summary-count">{count}</span>
            </div>
          );
        })}
        <div className="low-stock-summary-chip low-stock-summary-total">
          <span className="low-stock-summary-brand">Total</span>
          <span className="low-stock-summary-count">{totalVisible}</span>
        </div>
      </div>

      <div className="low-stock-controls">
        <input
          className="low-stock-search"
          type="text"
          placeholder="Search SKU or name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {totalVisible === 0 && (
        <div className="no-data">No items match your search.</div>
      )}

      {BRANDS.map((brand) => {
        const items = filteredBrands[brand] || [];
        if (items.length === 0) return null;
        const isCollapsed = collapsed[brand];

        return (
          <div key={brand} className="low-stock-brand-section">
            <button className="low-stock-brand-header" onClick={() => toggleCollapse(brand)}>
              <span className="low-stock-brand-name">{brand}</span>
              <span className="low-stock-brand-count">{items.length} items</span>
              <span className="low-stock-chevron">{isCollapsed ? '▶' : '▼'}</span>
            </button>

            {!isCollapsed && (
              <div className="low-stock-table-wrap">
                <table className="low-stock-table">
                  <thead>
                    <tr>
                      <th className="low-stock-th low-stock-th-check">Inactivate in NetSuite</th>
                      <SortHeader label="SKU" col="sku" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortHeader label="Name" col="name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortHeader label="HQ" col="qty_hq" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Charleston" col="qty_chs" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Boston" col="qty_bos" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                      <SortHeader label="Total" col="qty_total" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} align="right" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const isSelected = selected.has(item.sku);
                      return (
                        <tr
                          key={item.sku}
                          className={`low-stock-row ${isSelected ? 'low-stock-row-selected' : ''}`}
                          onClick={() => navigate(`/ops/${item.sku}`)}
                          title="Go to item"
                        >
                          <td className="low-stock-td-check" onClick={(e) => toggleItem(item.sku, e)}>
                            <input
                              type="checkbox"
                              className="low-stock-checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                            />
                          </td>
                          <td className="low-stock-sku">
                            {item.ns_url
                              ? <a href={item.ns_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="low-stock-ns-link">{item.sku}</a>
                              : item.sku}
                          </td>
                          <td className="low-stock-name">{item.name}</td>
                          <td className="low-stock-num">{qty(item.qty_hq)}</td>
                          <td className="low-stock-num">{qty(item.qty_chs)}</td>
                          <td className="low-stock-num">{qty(item.qty_bos)}</td>
                          <td className="low-stock-num" style={{ fontWeight: 600 }}>{qty(item.qty_total)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {selected.size > 0 && (
        <div className="low-stock-bulk-bar">
          <span className="low-stock-bulk-count">{selected.size} selected</span>
          <div className="low-stock-bulk-actions">
            <button
              className="low-stock-bulk-btn low-stock-bulk-inactivate"
              onClick={handleInactivate}
              disabled={bulkLoading}
            >
              {bulkLoading ? 'Inactivating...' : 'Mark Inactive'}
            </button>
            <button
              className="low-stock-bulk-btn low-stock-bulk-clear"
              onClick={clearSelection}
              disabled={bulkLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SortHeader({ label, col, sortKey, sortDir, onSort, align }) {
  const active = sortKey === col;
  return (
    <th
      className={`low-stock-th ${align === 'right' ? 'low-stock-th-right' : ''} ${active ? 'low-stock-th-active' : ''}`}
      onClick={() => onSort(col)}
    >
      {label}
      {active && <span className="low-stock-sort-arrow">{sortDir === 'asc' ? ' ↑' : ' ↓'}</span>}
    </th>
  );
}

export default StockByLocationDashboard;
